<template>
  <div>
    <MdmHeaderTitle
      :description="fullyEnrolled ? enrollText.fullEnrollDescription :
        enrollText.enrollDescription"
      :title="fullyEnrolled ? enrollText.fullEnrollTitle :
        enrollText.enrollTitle"
    />
    <div
      v-if="!fullyEnrolled"
      :class="$style.unenrolledSystems"
    >
      <a
        href="javascript:void(0);"
        @click.prevent="handleRedirectSystems"
      >view unenrolled {{ deviceItem.labelPlural }}</a>
    </div>
    <SummaryGraph
      v-if="shouldShowGraph"
      :categories="categories"
      :item="deviceItem.labelPlural"
      totalText="macOS devices"
    />
    <RecommendedPolicyBanner
      v-if="showRecommendedPolicies"
      :policyDescription="
        `Remotely enroll ${deviceItem.plural} in JumpCloud's MDM across your organization.`"
      :policyTitle="`JumpCloud MDM Enrollment ${configurationItem.title}`"
      policyOs="darwin"
      policyTemplateName="mdm_darwin"
      :showHeader="false"
      @no-policies-left="handleNoPoliciesLeft"
    />
    <MdmPolicyBanner v-else />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Constants, { ItemTypeConsts, MdmVendorStates, StoreModuleMap } from 'client/util/Constants';
import ItemType from 'client/util/Constants/ItemType';
import MdmHeaderTitle from 'client/MDM/components/MdmHeaderTitle';
import MdmPolicyBanner from 'client/MDM/components/MdmPolicyBanner';
import MdmSettingsMixin from 'client/MDM/mixins/MdmSettingsMixin';
import RecommendedPolicyBanner from 'client/policies/RecommendedPolicyBanner';
import SummaryGraph from 'client/components/Graphs/SummaryGraph';
import SummaryGraphCategory from 'client/components/Graphs/SummaryGraphCategory';
import SystemFilters from 'client/components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import Util from 'client/util/Util';

const { DeviceItemType, ConfigurationItemType } = ItemType;
const deviceItem = new DeviceItemType();
const configurationItem = new ConfigurationItemType();

const {
  macOSX,
  filterNames,
} = Constants;

const {
  internal,
  external,
  none,
} = MdmVendorStates;

const systemCollection = StoreModuleMap[ItemTypeConsts.system];

export default {
  name: 'MdmSettingsHeader',

  components: {
    MdmHeaderTitle,
    MdmPolicyBanner,
    RecommendedPolicyBanner,
    SummaryGraph,
  },

  mixins: [MdmSettingsMixin],

  data() {
    return {
      categories: [],
      configurationItem,
      deviceItem,
      showRecommendedPolicies: true,
      macSystemsCount: 0,
      jcEnrolledCount: 0,
      otherVendorCount: 0,
      notEnrolledCount: 0,
    };
  },

  computed: {
    enrollText() {
      return {
        enrollTitle: `Enroll ${deviceItem.titlePlural}`,
        enrollDescription: `Enroll all of your Mac ${deviceItem.labelPlural} in a few clicks using the JumpCloud MDM Enrollment policy.`,
        fullEnrollTitle: 'Great job! MDM is completely rolled out.',
        fullEnrollDescription: `All of your macOS ${deviceItem.labelPlural} are enrolled in JumpCloud's MDM.`,
      };
    },

    fullyEnrolled() {
      return this.macSystemsCount > 0 && this.macSystemsCount === this.jcEnrolledCount;
    },

    shouldShowGraph() {
      return this.macSystemsCount > 0;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(systemCollection, [
      'setFilter',
      'setFilters',
      'deleteFilter',
      'fetchTotalCount',
    ]),

    async handleMounted() {
      this.setFilter({ filter: SystemFilters.os({ os: macOSX }) });
      this.fetchTotalCount()
        .then((totalCount) => {
          this.macSystemsCount = totalCount;
          this.setFilter({ filter: SystemFilters.mdm(internal) });
          return this.fetchTotalCount();
        })
        .then((jcEnrolledCount) => {
          this.jcEnrolledCount = jcEnrolledCount;
          this.setFilter({ filter: SystemFilters.mdm(external) });
          return this.fetchTotalCount();
        })
        .then((otherVendorCount) => {
          this.otherVendorCount = otherVendorCount;
          this.setFilter({ filter: SystemFilters.mdm(none) });
          return this.fetchTotalCount();
        })
        .then((notEnrolledCount) => {
          this.notEnrolledCount = notEnrolledCount;
          this.deleteFilter({ filterName: filterNames.mdmVendor });
          this.deleteFilter({ filterName: filterNames.os });
          if (this.macSystemsCount > 0) this.setCategories();
          this.$emit('mounted');
        });
    },

    handleNoPoliciesLeft() {
      this.showRecommendedPolicies = false;
    },

    handleRedirectSystems() {
      this.setFilters({
        filtersArray: [
          SystemFilters.os({ os: macOSX }),
          SystemFilters.mdm(none),
        ],
      });

      Util.redirect(deviceItem.route);
    },

    setCategories() {
      const categories = [];

      if (this.jcEnrolledCount > 0) {
        const jcEnrolledCategory = new SummaryGraphCategory({
          name: 'enrolled with JumpCloud',
          qty: this.jcEnrolledCount,
          style: 'lightGreen',
        });

        categories.push(jcEnrolledCategory);
      }

      if (this.otherVendorCount > 0) {
        const otherVendorCategory = new SummaryGraphCategory({
          name: 'enrolled with another vendor',
          qty: this.otherVendorCount,
          style: 'teal',
        });

        categories.push(otherVendorCategory);
      }

      if (this.notEnrolledCount > 0) {
        const notEnrolledCategory = new SummaryGraphCategory({
          name: 'not enrolled',
          qty: this.notEnrolledCount,
          style: 'lightGrey',
        });

        categories.push(notEnrolledCategory);
      }

      this.categories = categories;
    },
  },
};
</script>

<style module>
  .unenrolledSystems {
    margin-left: 20px;
  }
</style>
